import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import '../assets/css/libs.css'
import '../assets/css/style.css'
import { AppState } from '../store'

interface FinancialCalcReversalResultProps {
  isResponsive: boolean
  hover: HoverPayload | null
  isMonthlyWithdrawal: boolean
  disSavingRate: number | string | null
  addition: number | string | null
  setHover: React.Dispatch<React.SetStateAction<HoverPayload | null>>
}

export type HoverPayload = {
  afterAge: number
}

function formatMonths(months: number, startAge: number) {
  const year = startAge + Math.floor(months / 12)
  const month = months % 12
  return { year: `${year}`, month: `${month}` }
}

export const FinancialCalcReversalResultComponent: React.FC<FinancialCalcReversalResultProps> = ({
  isResponsive,
  hover,
  isMonthlyWithdrawal,
  disSavingRate,
  addition,
  setHover,
}) => {
  const result = useSelector((state: AppState) => state.financialCalcReversal.result)

  const resultRef = React.useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (result) {
      setTimeout(() => {
        resultRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }, 500)
    }
  }, [result])

  useEffect(() => {
    setHover(null)
  }, [result, setHover])

  const formatMonthsStr = useMemo(() => {
    const rate = result?.rate
      ? {
          deposit: result.rate?.depositMonths
            ? formatMonths(result.rate.depositMonths, result.startAge)
            : { year: '120+', month: '---' },
          investment: result.rate?.investmentMonths
            ? formatMonths(result.rate.investmentMonths, result.startAge)
            : { year: '120+', month: '---' },
        }
      : {
          deposit: { year: '---', month: '---' },
          investment: { year: '---', month: '---' },
        }

    const fixed = result?.fixed
      ? {
          deposit: result.fixed?.depositMonths
            ? formatMonths(result.fixed.depositMonths, result.startAge)
            : { year: '120+', month: '---' },
          investment: result.fixed?.investmentMonths
            ? formatMonths(result.fixed.investmentMonths, result.startAge)
            : { year: '120+', month: '---' },
        }
      : {
          deposit: { year: '---', month: '---' },
          investment: { year: '---', month: '---' },
        }

    return { rate, fixed }
  }, [result])

  const formatTenThousand = (value: number | undefined) => {
    if (value === undefined) return '---'
    const num = Math.round(value / 10000)

    return num.toLocaleString('ja-JP')
  }

  const defaultAfterAge = 20

  const [rateAfterAge, setRateAfterAge] = React.useState<number>(defaultAfterAge)
  const [fixedAfterAge, setFixedAfterAge] = React.useState<number>(defaultAfterAge)

  const increaseAfterAge = (setAfterAge: React.Dispatch<React.SetStateAction<number>>) => {
    if (!result?.startAge) return
    setAfterAge((prev) => {
      if (prev + result.startAge < 120) {
        return prev + 1
      }
      return prev
    })
  }

  const decreaseAfterAge = (setAfterAge: React.Dispatch<React.SetStateAction<number>>) => {
    if (!result?.startAge) return
    setAfterAge((prev) => (prev > 0 ? prev - 1 : prev))
  }

  const increaseRateAfterAge = () => {
    increaseAfterAge(setRateAfterAge)
  }
  const decreaseRateAfterAge = () => {
    decreaseAfterAge(setRateAfterAge)
  }
  const increaseFixedAfterAge = () => {
    increaseAfterAge(setFixedAfterAge)
  }
  const decreaseFixedAfterAge = () => {
    decreaseAfterAge(setFixedAfterAge)
  }

  useEffect(() => {
    if (hover && result) {
      setRateAfterAge(hover.afterAge)
      setFixedAfterAge(hover.afterAge)
    } else if (result) {
      setRateAfterAge(defaultAfterAge)
      setFixedAfterAge(defaultAfterAge)
    }
  }, [hover, result])

  return (
    <section
      className="u-bg--green"
      id="financial-calc-reversal-result"
      ref={resultRef}
      style={{ display: result ? 'block' : 'none' }}
    >
      {/* 定率で取り崩し */}
      {result && (
        <div className="u-pt60px">
          <section className="c-box">
            <h3 className="c-box__head">
              <span className="c-box__head__title">
                定率
                <span>{result?.rate ? disSavingRate ?? '---' : '---'}%</span>で
                <br className="sm_only"></br>取り崩し
              </span>
            </h3>
            <div className="p-simulation__grid-2 reversal_kekka_teritsu">
              <div className="p-simulation__grid-2__col">
                <div className="reversal_kekka">
                  {result?.rate ? result.startAge + rateAfterAge : '---'}歳<span>時点</span>
                </div>
                <div className="reversal_kekka_age_btn">
                  <button
                    className="up"
                    onClick={increaseRateAfterAge}
                    disabled={result?.rate ? result.startAge + rateAfterAge >= 120 : true}
                  >
                    ▲
                  </button>
                  <button
                    className="down"
                    onClick={decreaseRateAfterAge}
                    disabled={result?.rate ? rateAfterAge <= 0 : true}
                  >
                    ▼
                  </button>
                </div>
              </div>
              <div className="p-simulation__grid-2__col">
                <table className="compare-table">
                  <thead>
                    <tr>
                      <th className="none"></th>
                      <th className="bg">
                        年率{result?.rate ? result.interest : '---'}
                        %で運用ができた場合
                      </th>
                      <th className="bg">運用しない場合</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <th>{isMonthlyWithdrawal ? '毎月' : '隔月'}取り崩し額</th>
                      <td>
                        約
                        <span className="big">
                          {formatTenThousand(result?.rate?.investmentList[rateAfterAge]?.addition)}
                        </span>
                        万円
                      </td>
                      <td>
                        約
                        <span className="big">
                          {formatTenThousand(result?.rate?.depositList[rateAfterAge]?.addition)}
                        </span>
                        万円
                      </td>
                    </tr>
                    <tr>
                      <th>保有資産残高</th>
                      <td className="shisan">
                        約
                        <span className="big">
                          {formatTenThousand(result?.rate?.investmentList[rateAfterAge]?.amount)}
                        </span>
                        万円
                      </td>
                      <td className="shisan">
                        約
                        <span className="big">
                          {formatTenThousand(result?.rate?.depositList[rateAfterAge]?.amount)}
                        </span>
                        万円
                      </td>
                    </tr>
                    <tr>
                      <th>資産寿命</th>
                      <td className="shisan">
                        <span className="big">{formatMonthsStr.rate.investment.year}</span>歳
                        <span className="big">{formatMonthsStr.rate.investment.month}</span>
                        ヶ月
                      </td>
                      <td className="shisan">
                        <span className="big">{formatMonthsStr.rate.deposit.year}</span>歳
                        <span className="big">{formatMonthsStr.rate.deposit.month}</span>
                        ヶ月
                      </td>
                    </tr>
                  </tbody>
                </table>
                {/* SP版 */}
                <div className="compare-table_sp">
                  <h4 className="u-mt20px">
                    年率{result?.rate ? result.interest : '---'}
                    %で運用ができた場合
                  </h4>
                  <div className="compare-table_sp_column">
                    <span className="compare-table_sp_column_ttl">
                      {isMonthlyWithdrawal ? '毎月' : '隔月'}取り崩し額
                    </span>
                    <span className="compare-table_sp_column_content">
                      約
                      <span className="big">
                        {formatTenThousand(result?.rate?.investmentList[rateAfterAge]?.addition)}
                      </span>
                      万円
                    </span>
                  </div>
                  <div className="compare-table_sp_column">
                    <span className="compare-table_sp_column_ttl">保有資産残高</span>
                    <span className="compare-table_sp_column_content">
                      約
                      <span className="big">
                        {formatTenThousand(result?.rate?.investmentList[rateAfterAge]?.amount)}
                      </span>
                      万円
                    </span>
                  </div>
                  <div className="compare-table_sp_column">
                    <span className="compare-table_sp_column_ttl">資産寿命</span>
                    <span className="compare-table_sp_column_content">
                      <span className="big">{formatMonthsStr.rate.investment.year}</span>歳
                      <span className="big">{formatMonthsStr.rate.investment.month}</span>
                      ヶ月
                    </span>
                  </div>
                  <h4 className="u-mt20px">運用しない場合</h4>
                  <div className="compare-table_sp_column">
                    <span className="compare-table_sp_column_ttl">
                      {isMonthlyWithdrawal ? '毎月' : '隔月'}取り崩し額
                    </span>
                    <span className="compare-table_sp_column_content">
                      約
                      <span className="big">
                        {formatTenThousand(result?.rate?.depositList[rateAfterAge]?.addition)}
                      </span>
                      万円
                    </span>
                  </div>
                  <div className="compare-table_sp_column">
                    <span className="compare-table_sp_column_ttl">保有資産残高</span>
                    <span className="compare-table_sp_column_content">
                      約
                      <span className="big">
                        {formatTenThousand(result?.rate?.depositList[rateAfterAge]?.amount)}
                      </span>
                      万円
                    </span>
                  </div>
                  <div className="compare-table_sp_column">
                    <span className="compare-table_sp_column_ttl">資産寿命</span>
                    <span className="compare-table_sp_column_content">
                      <span className="big">{formatMonthsStr.rate.deposit.year}</span>歳
                      <span className="big">{formatMonthsStr.rate.deposit.month}</span>
                      ヶ月
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <div className="u-pt60px">
            <section className="c-box">
              <h3 className="c-box__head">
                <span className="c-box__head__title">
                  定額
                  <span>{result?.fixed ? addition ?? '---' : '---'}万円</span>で
                  <br className="sm_only"></br>取り崩し
                </span>
              </h3>
              <div className="p-simulation__grid-2 reversal_kekka_teigaku">
                <div className="p-simulation__grid-2__col">
                  <div className="reversal_kekka">
                    {result?.fixed ? result.startAge + fixedAfterAge : '---'}歳<span>時点</span>
                  </div>
                  <div className="reversal_kekka_age_btn">
                    <button
                      className="up"
                      onClick={increaseFixedAfterAge}
                      disabled={result?.fixed ? result.startAge + fixedAfterAge >= 120 : true}
                    >
                      ▲
                    </button>
                    <button
                      className="down"
                      onClick={decreaseFixedAfterAge}
                      disabled={result?.fixed ? fixedAfterAge <= 0 : true}
                    >
                      ▼
                    </button>
                  </div>
                </div>
                <div className="p-simulation__grid-2__col">
                  <table className="compare-table">
                    <thead>
                      <tr>
                        <th className="none"></th>
                        <th className="bg">
                          年率{result?.fixed ? result.interest : '---'}
                          %で運用ができた場合
                        </th>
                        <th className="bg">運用しない場合</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <th>{isMonthlyWithdrawal ? '毎月' : '隔月'}取り崩し額</th>
                        <td>
                          <span className="big">{result?.fixed ? addition ?? '---' : '---'}</span>
                          万円
                        </td>
                        <td>
                          <span className="big">{result?.fixed ? addition ?? '---' : '---'}</span>
                          万円
                        </td>
                      </tr>
                      <tr>
                        <th>保有資産残高</th>
                        <td className="shisan">
                          約
                          <span className="big">
                            {formatTenThousand(
                              result?.fixed?.investmentList[fixedAfterAge]?.amount
                            )}
                          </span>
                          万円
                        </td>
                        <td className="shisan">
                          約
                          <span className="big">
                            {formatTenThousand(result?.fixed?.depositList[fixedAfterAge]?.amount)}
                          </span>
                          万円
                        </td>
                      </tr>
                      <tr>
                        <th>資産寿命</th>
                        <td className="shisan">
                          <span className="big">{formatMonthsStr.fixed.investment.year}</span>歳
                          <span className="big">{formatMonthsStr.fixed.investment.month}</span>
                          ヶ月
                        </td>
                        <td className="shisan">
                          <span className="big">{formatMonthsStr.fixed.deposit.year}</span>歳
                          <span className="big">{formatMonthsStr.fixed.deposit.month}</span>
                          ヶ月
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {/* SP版 */}
                  <div className="compare-table_sp">
                    <h4 className="u-mt20px">
                      年率{result?.fixed ? result.interest : '---'}
                      %で運用ができた場合
                    </h4>
                    <div className="compare-table_sp_column">
                      <span className="compare-table_sp_column_ttl">
                        {isMonthlyWithdrawal ? '毎月' : '隔月'}取り崩し額
                      </span>
                      <span className="compare-table_sp_column_content">
                        <span className="big">{result?.fixed ? addition ?? '---' : '---'}</span>
                        万円
                      </span>
                    </div>
                    <div className="compare-table_sp_column">
                      <span className="compare-table_sp_column_ttl">保有資産残高</span>
                      <span className="compare-table_sp_column_content">
                        約
                        <span className="big">
                          {formatTenThousand(result?.fixed?.investmentList[fixedAfterAge]?.amount)}
                        </span>
                        万円
                      </span>
                    </div>
                    <div className="compare-table_sp_column">
                      <span className="compare-table_sp_column_ttl">資産寿命</span>
                      <span className="compare-table_sp_column_content">
                        <span className="big">{formatMonthsStr.fixed.investment.year}</span>歳
                        <span className="big">{formatMonthsStr.fixed.investment.month}</span>
                        ヶ月
                      </span>
                    </div>
                    <h4 className="u-mt20px">運用しない場合</h4>
                    <div className="compare-table_sp_column">
                      <span className="compare-table_sp_column_ttl">
                        {isMonthlyWithdrawal ? '毎月' : '隔月'}取り崩し額
                      </span>
                      <span className="compare-table_sp_column_content">
                        <span className="big">{result?.fixed ? addition ?? '---' : '---'}</span>
                        万円
                      </span>
                    </div>
                    <div className="compare-table_sp_column">
                      <span className="compare-table_sp_column_ttl">保有資産残高</span>
                      <span className="compare-table_sp_column_content">
                        約
                        <span className="big">
                          {formatTenThousand(result?.fixed?.depositList[fixedAfterAge]?.amount)}
                        </span>
                        万円
                      </span>
                    </div>
                    <div className="compare-table_sp_column">
                      <span className="compare-table_sp_column_ttl">資産寿命</span>
                      <span className="compare-table_sp_column_content">
                        <span className="big">{formatMonthsStr.fixed.deposit.year}</span>歳
                        <span className="big">{formatMonthsStr.fixed.deposit.month}</span>
                        ヶ月
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <p className="teigaku_note">
              シミュレーション結果は、1万円未満を四捨五入して表示をしているため、実際の残高と一致しない場合があります。
            </p>
          </div>
        </div>
      )}
    </section>
  )
}
